import { useCallback } from "react";
import { useURLState } from "./useURLState";
import { getDetailPath } from "../utils/navigationUtils";
import { CREATIVE_ROUTE_CONFIG } from "../config/routeConfig";

/**
 * Navigation hook for the Creative system (flat 4-tab model)
 *
 * Tab keys: displayUnits | displayGroups | videoUnits | videoGroups
 */
export const useCreativeNavigation = () => {
  const { urlState, updateURL, history, location } = useURLState();

  const navigate = useCallback((options = {}) => {
    const {
      tab = urlState.tab,
      page = urlState.pageNumber,
      type = 'replace',
      detail = null,
    } = options;

    if (detail) {
      const { type: detailType, id, mediaType = 'DISPLAY' } = detail;
      const detailPath = getDetailPath(detailType, mediaType);
      const currentUrl = `${location.pathname}${location.search}`;
      const returnUrl = encodeURIComponent(currentUrl);
      history.push(`${detailPath}/${id}?returnUrl=${returnUrl}`);
    } else {
      updateURL({ tab, page }, { type });
    }
  }, [urlState, updateURL, history, location]);

  const navigateToTab = useCallback((tab) => {
    const flatTab = CREATIVE_ROUTE_CONFIG.validTabs.includes(tab) ? tab : urlState.tab;
    navigate({ tab: flatTab, page: 1 });
  }, [navigate, urlState.tab]);

  const navigateToPage = useCallback((page) => {
    navigate({ page });
  }, [navigate]);

  const navigateToDetail = useCallback((detailType, id, mediaType = 'DISPLAY') => {
    navigate({ detail: { type: detailType, id, mediaType } });
  }, [navigate]);

  const navigateBack = useCallback((fallbackContext = {}) => {
    const { returnUrl } = urlState;
    if (returnUrl) {
      history.push(decodeURIComponent(returnUrl));
    } else {
      const { mediaType = 'DISPLAY', isAdGroup = false } = fallbackContext;
      const tab = mediaType === 'VIDEO'
        ? (isAdGroup ? 'videoGroups' : 'videoUnits')
        : (isAdGroup ? 'displayGroups' : 'displayUnits');
      navigate({ tab, page: 1 });
    }
  }, [urlState, history, navigate]);

  const validateRoute = useCallback(() => {
    const { tab, pageNumber } = urlState;
    const isValid = CREATIVE_ROUTE_CONFIG.validTabs.includes(tab) && pageNumber > 0;
    if (!isValid) {
      navigate({ tab: CREATIVE_ROUTE_CONFIG.defaultRoute.tab, page: 1 });
      return false;
    }
    return true;
  }, [urlState, navigate]);

  // Helpers derived from the flat tab key
  const getNavigationHelpers = useCallback(() => {
    const { tab } = urlState;
    return {
      isDisplayAds: tab === 'displayUnits' || tab === 'displayGroups',
      isVideoAds: tab === 'videoUnits' || tab === 'videoGroups',
      isAdUnits: tab === 'displayUnits' || tab === 'videoUnits',
      isAdGroups: tab === 'displayGroups' || tab === 'videoGroups',
      mediaType: (tab === 'videoUnits' || tab === 'videoGroups') ? 'VIDEO' : 'DISPLAY',
    };
  }, [urlState]);

  return {
    currentState: urlState,
    navigate,
    navigateToTab,
    navigateToPage,
    navigateToDetail,
    navigateBack,
    validateRoute,
    getNavigationHelpers,
  };
};

export default useCreativeNavigation;
